import React, { useState, useMemo } from 'react';
import { 
  Card, Table, Tag, Button, Progress, Tooltip, Empty, Space, Typography
} from 'antd';
import { 
  ArrowLeftOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  UserOutlined
} from '@ant-design/icons';

const { Text } = Typography;

const TrackedDriverPerformanceTab = ({ 
  driverData, 
  stopData, 
  tripData,
  tripStartTimes 
}) => {
  const [selectedDriverDetail, setSelectedDriverDetail] = useState(null);
  const [driverTripData, setDriverTripData] = useState([]);

  // Helper to check if a stop is tracked
  const isStopTracked = (stop) => {
    const hasActualDeparture = Number.isFinite(stop.ACTUAL_DEPARTURE_TIME);
    const hasActualArrival = Number.isFinite(stop.ACTUAL_ARRIVAL_TIME);
    return hasActualDeparture || hasActualArrival;
  };

  // Calculate tracking statistics for drivers
  const trackedDriverData = useMemo(() => {
    if (!stopData || stopData.length === 0) return [];
    
    const driverStats = {};
    
    stopData.forEach(stop => {
      const driverId = stop.DRIVER_ID;
      if (!driverId) return;
      
      if (!driverStats[driverId]) {
        driverStats[driverId] = {
          driverId,
          totalStops: 0,
          trackedStops: 0,
          untrackedStops: 0,
          firstStops: 0,
          midStops: 0,
          lastStops: 0,
          trackedFirstStops: 0,
          trackedMidStops: 0,
          trackedLastStops: 0,
          routes: new Set(),
          trips: new Set(),
          dates: new Set()
        };
      }
      
      const driver = driverStats[driverId];
      driver.totalStops++;
      driver.routes.add(stop.ROUTE);
      driver.trips.add(stop.TRIP_ID);
      driver.dates.add(stop.DATE_ID);
      
      // Track stop types
      if (stop.TSN_FIRST_LAST_IND === 'F') {
        driver.firstStops++;
        if (isStopTracked(stop)) driver.trackedFirstStops++;
      } else if (stop.TSN_FIRST_LAST_IND === 'L') {
        driver.lastStops++;
        if (isStopTracked(stop)) driver.trackedLastStops++;
      } else {
        driver.midStops++;
        if (isStopTracked(stop)) driver.trackedMidStops++;
      }
      
      // Track overall
      if (isStopTracked(stop)) {
        driver.trackedStops++;
      } else {
        driver.untrackedStops++;
      }
    });
    
    // Calculate percentages and convert sets to arrays
    return Object.values(driverStats).map(driver => ({
      ...driver,
      trackingCoverage: (driver.trackedStops / driver.totalStops) * 100,
      untrackedPercent: (driver.untrackedStops / driver.totalStops) * 100,
      firstStopTrackingPercent: driver.firstStops > 0 ? (driver.trackedFirstStops / driver.firstStops) * 100 : 0,
      midStopTrackingPercent: driver.midStops > 0 ? (driver.trackedMidStops / driver.midStops) * 100 : 0,
      lastStopTrackingPercent: driver.lastStops > 0 ? (driver.trackedLastStops / driver.lastStops) * 100 : 0,
      routeCount: driver.routes.size,
      tripCount: driver.trips.size,
      dateCount: driver.dates.size,
      routes: Array.from(driver.routes),
      trips: Array.from(driver.trips)
    }));
  }, [stopData]);

  if (trackedDriverData.length === 0) {
    return (
      <Empty 
        description="No driver data available" 
        image={Empty.PRESENTED_IMAGE_SIMPLE} 
      />
    );
  }

  const handleDriverClick = async (driverId) => {
    setSelectedDriverDetail(driverId);
    
    // Process trip data for tracking analysis for this driver
    const driverTrips = {};
    
    // Filter stops for this driver and group by trip
    const driverStops = stopData.filter(stop => stop.DRIVER_ID === driverId);
    
    driverStops.forEach(stop => {
      const tripId = stop.TRIP_ID;
      if (!driverTrips[tripId]) {
        driverTrips[tripId] = {
          tripId,
          route: stop.ROUTE,
          driverId,
          totalStops: 0,
          trackedStops: 0,
          untrackedStops: 0,
          firstStops: 0,
          midStops: 0,
          lastStops: 0,
          trackedFirstStops: 0,
          trackedMidStops: 0,
          trackedLastStops: 0,
          dates: new Set()
        };
      }
      
      const trip = driverTrips[tripId];
      trip.totalStops++;
      trip.dates.add(stop.DATE_ID);
      
      // Track stop types
      if (stop.TSN_FIRST_LAST_IND === 'F') {
        trip.firstStops++;
        if (isStopTracked(stop)) trip.trackedFirstStops++;
      } else if (stop.TSN_FIRST_LAST_IND === 'L') {
        trip.lastStops++;
        if (isStopTracked(stop)) trip.trackedLastStops++;
      } else {
        trip.midStops++;
        if (isStopTracked(stop)) trip.trackedMidStops++;
      }
      
      if (isStopTracked(stop)) {
        trip.trackedStops++;
      } else {
        trip.untrackedStops++;
      }
    });
    
    // Calculate percentages and convert to array
    const processedTrips = Object.values(driverTrips).map(trip => ({
      ...trip,
      trackingCoverage: (trip.trackedStops / trip.totalStops) * 100,
      firstStopTrackingPercent: trip.firstStops > 0 ? (trip.trackedFirstStops / trip.firstStops) * 100 : 0,
      midStopTrackingPercent: trip.midStops > 0 ? (trip.trackedMidStops / trip.midStops) * 100 : 0,
      lastStopTrackingPercent: trip.lastStops > 0 ? (trip.trackedLastStops / trip.lastStops) * 100 : 0,
      dateCount: trip.dates.size
    }));
    
    setDriverTripData(processedTrips);
  };

  const renderDriverTripView = () => {
    if (!selectedDriverDetail || driverTripData.length === 0) {
      return null;
    }

    const columns = [
      {
        title: 'Trip Start',
        dataIndex: 'tripId',
        key: 'tripId',
        sorter: (a, b) => String(a.tripId || '').localeCompare(String(b.tripId || '')),
        render: (_, record) => tripStartTimes[record.tripKey]?.time || record.tripId,
      },
      {
        title: 'Route',
        dataIndex: 'route',
        key: 'route',
        sorter: (a, b) => String(a.route || '').localeCompare(String(b.route || '')),
      },
      {
        title: 'Tracking Coverage %',
        dataIndex: 'trackingCoverage',
        key: 'trackingCoverage',
        render: val => {
          const value = val || 0;
          let color = 'green';
          if (value < 50) color = '#f5222d';
          else if (value < 80) color = '#fa8c16';
          
          return (
            <Tooltip title={`${Math.round(value)}% tracked`}>
              <Progress 
                percent={Math.round(value)} 
                size="small" 
                strokeColor={color}
                format={percent => `${percent}%`}
              />
            </Tooltip>
          );
        },
        sorter: (a, b) => (a.trackingCoverage || 0) - (b.trackingCoverage || 0),
      },
      {
        title: 'First Stop Tracking',
        dataIndex: 'firstStopTrackingPercent',
        key: 'firstStopTracking',
        render: val => `${Math.round(val || 0)}%`,
        sorter: (a, b) => (a.firstStopTrackingPercent || 0) - (b.firstStopTrackingPercent || 0),
      },
      {
        title: 'Mid Stop Tracking',
        dataIndex: 'midStopTrackingPercent',
        key: 'midStopTracking',
        render: val => `${Math.round(val || 0)}%`,
        sorter: (a, b) => (a.midStopTrackingPercent || 0) - (b.midStopTrackingPercent || 0),
      },
      {
        title: 'Last Stop Tracking',
        dataIndex: 'lastStopTrackingPercent',
        key: 'lastStopTracking',
        render: val => `${Math.round(val || 0)}%`,
        sorter: (a, b) => (a.lastStopTrackingPercent || 0) - (b.lastStopTrackingPercent || 0),
      },
      {
        title: 'Total Stops',
        dataIndex: 'totalStops',
        key: 'totalStops',
        sorter: (a, b) => (a.totalStops || 0) - (b.totalStops || 0),
      },
      {
        title: 'Tracked',
        dataIndex: 'trackedStops',
        key: 'trackedStops',
        render: val => (
          <Tag color="green" icon={<EyeOutlined />}>
            {val}
          </Tag>
        ),
        sorter: (a, b) => (a.trackedStops || 0) - (b.trackedStops || 0),
      },
      {
        title: 'Untracked',
        dataIndex: 'untrackedStops',
        key: 'untrackedStops',
        render: val => (
          <Tag color="red" icon={<EyeInvisibleOutlined />}>
            {val}
          </Tag>
        ),
        sorter: (a, b) => (a.untrackedStops || 0) - (b.untrackedStops || 0),
      }
    ];

    return (
      <Card 
        title={
          <Space>
            <UserOutlined />
            <span>Trip Tracking Performance for Driver {selectedDriverDetail}</span>
          </Space>
        }
        extra={
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => setSelectedDriverDetail(null)}
          >
            Back to Drivers
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={driverTripData}
          rowKey="tripId"
          pagination={{ pageSize: 20 }}
        />
      </Card>
    );
  };

  const columns = [
    {
      title: 'Driver ID',
      dataIndex: 'driverId',
      key: 'driverId',
      sorter: (a, b) => String(a.driverId || '').localeCompare(String(b.driverId || '')),
      render: (text, record) => (
        <Button 
          type="link" 
          onClick={() => handleDriverClick(record.driverId)}
          style={{
            fontSize: '14px',
            fontWeight: 'bold',
            textDecoration: 'underline'
          }}
        >
          {text}
        </Button>
      ),
    },
    {
      title: 'Tracking Coverage %',
      dataIndex: 'trackingCoverage',
      key: 'trackingCoverage',
      render: (val, record) => {
        const value = val || 0;
        let color = 'green';
        if (value < 50) color = '#f5222d';
        else if (value < 80) color = '#fa8c16';
        
        return (
          <Tooltip title={`${Math.round(value)}% tracked\n${record.trackedStops} tracked stops\n${record.untrackedStops} untracked stops`}>
            <Progress 
              percent={Math.round(value)} 
              size="small" 
              strokeColor={color}
              format={percent => `${percent}%`}
            />
          </Tooltip>
        );
      },
      sorter: (a, b) => (a.trackingCoverage || 0) - (b.trackingCoverage || 0),
    },
    {
      title: 'First Stop Tracking %',
      dataIndex: 'firstStopTrackingPercent',
      key: 'firstStopTracking',
      render: val => `${Math.round(val || 0)}%`,
      sorter: (a, b) => (a.firstStopTrackingPercent || 0) - (b.firstStopTrackingPercent || 0),
    },
    {
      title: 'Mid Stop Tracking %',
      dataIndex: 'midStopTrackingPercent',
      key: 'midStopTracking',
      render: val => `${Math.round(val || 0)}%`,
      sorter: (a, b) => (a.midStopTrackingPercent || 0) - (b.midStopTrackingPercent || 0),
    },
    {
      title: 'Last Stop Tracking %',
      dataIndex: 'lastStopTrackingPercent',
      key: 'lastStopTracking',
      render: val => `${Math.round(val || 0)}%`,
      sorter: (a, b) => (a.lastStopTrackingPercent || 0) - (b.lastStopTrackingPercent || 0),
    },
    {
      title: 'Total Stops',
      dataIndex: 'totalStops',
      key: 'totalStops',
      sorter: (a, b) => (a.totalStops || 0) - (b.totalStops || 0),
    },
    {
      title: 'Tracked',
      dataIndex: 'trackedStops',
      key: 'trackedStops',
      render: val => (
        <Tag color="green" icon={<EyeOutlined />}>
          {val}
        </Tag>
      ),
      sorter: (a, b) => (a.trackedStops || 0) - (b.trackedStops || 0),
    },
    {
      title: 'Untracked',
      dataIndex: 'untrackedStops',
      key: 'untrackedStops',
      render: val => (
        <Tag color="red" icon={<EyeInvisibleOutlined />}>
          {val}
        </Tag>
      ),
      sorter: (a, b) => (a.untrackedStops || 0) - (b.untrackedStops || 0),
    },
    {
      title: 'Routes',
      dataIndex: 'routeCount',
      key: 'routeCount',
      render: (count, record) => (
        <Tooltip title={`Routes: ${record.routes.join(', ')}`}>
          <Tag color="blue">{count} route{count !== 1 ? 's' : ''}</Tag>
        </Tooltip>
      ),
      sorter: (a, b) => (a.routeCount || 0) - (b.routeCount || 0),
    },
    {
      title: 'Trips',
      dataIndex: 'tripCount',
      key: 'tripCount',
      render: count => (
        <Tag color="purple">{count} trip{count !== 1 ? 's' : ''}</Tag>
      ),
      sorter: (a, b) => (a.tripCount || 0) - (b.tripCount || 0),
    }
  ];

  return (
    <div>
      {selectedDriverDetail ? (
        renderDriverTripView()
      ) : (
        <Card 
          title={
            <Space>
              <UserOutlined />
              <span>Driver Tracking Performance</span>
              <Tooltip title="This table shows tracking coverage metrics for each driver. Tracking coverage indicates how often GPS or timing data is successfully captured for stops operated by each driver.">
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  (Click driver ID to see trip details)
                </Text>
              </Tooltip>
            </Space>
          }
        >
          <Table
            columns={columns}
            dataSource={trackedDriverData}
            rowKey="driverId"
            pagination={{ pageSize: 20 }}
          />
        </Card>
      )}
    </div>
  );
};

export default TrackedDriverPerformanceTab; 